import { useState, type ReactElement } from 'react'
import { useParams } from 'react-router-dom'
import { documentationRootGuid } from '@constants'
import { getFileUrl } from '@domainUtils'
import { Box, CircularProgress, IconButton } from '@mui/material'
import { MaterialSymbol } from '@ui2/icons'
import { EditableFileListTitle, FileListRow } from '@ui2/structure'
import { useRenameDocument } from '@editorHooks'
import DeleteButton from '../UploadForm/components/DeleteButton'
import { DOC_ICON, getDocKind, getFileFormatLabel } from './utils'

interface Props {
  file: FileItem
  /** Documentation root the file lives in (rename/delete target). */
  rootGuid?: string
  disableForm?: boolean
  selected?: boolean
  onClick?: () => void
  onDelete?: () => void
}

/**
 * Row for a project document: kind icon, click-to-rename title (on selectable
 * rows only once selected), download and delete actions. Used by Results2's Dokumente tab and the document manager.
 */
const DocumentFileRow = ({
  file,
  rootGuid = documentationRootGuid,
  disableForm = false,
  selected,
  onClick,
  onDelete,
}: Props): ReactElement => {
  const { projectId } = useParams()
  const meta = DOC_ICON[getDocKind(file)] ?? DOC_ICON.doc
  // Selectable rows (onClick given) only rename via the title once selected;
  // a click on an unselected row's title selects it instead.
  const canRenameByTitle = !disableForm && (!onClick || !!selected)

  const { mutate: renameDocument, isLoading: isRenaming } = useRenameDocument(rootGuid)
  const [isEditing, setIsEditing] = useState(false)

  const startEditing = () => {
    if (disableForm) return
    setIsEditing(true)
  }

  return (
    <FileListRow
      data-cy="document-file-row"
      selected={selected}
      onClick={onClick}
      icon={<MaterialSymbol icon={meta.icon} size={22} sx={{ color: meta.color }} />}
      title={
        <EditableFileListTitle
          name={file.name}
          isEditing={isEditing}
          onEditingChange={setIsEditing}
          onRename={name => renameDocument({ file, name })}
          canRenameByClick={canRenameByTitle}
        />
      }
      subtitle={getFileFormatLabel(file)}
      timestamp={file.created ?? undefined}
      actions={
        <>
          {!disableForm &&
            !isEditing &&
            (isRenaming ? (
              <Box p={0.5} display="flex">
                <CircularProgress size={16} />
              </Box>
            ) : (
              <IconButton
                size="small"
                onClick={event => {
                  event.stopPropagation()
                  startEditing()
                }}
              >
                <MaterialSymbol icon="edit" size={16} />
              </IconButton>
            ))}
          <IconButton
            size="small"
            onClick={event => {
              event.stopPropagation()
              if (projectId) window.open(getFileUrl(file, projectId, rootGuid), '_blank')
            }}
          >
            <MaterialSymbol icon="download" size={16} />
          </IconButton>
          {disableForm ? null : (
            <DeleteButton
              docGuid={file.guid}
              docName={file.name}
              rootGuid={rootGuid}
              onDelete={onDelete}
            />
          )}
        </>
      }
    />
  )
}

export default DocumentFileRow
